import { mediaRef } from '@/services/mediaAssets';

export const axesAndOs = {
  id: 'axes-and-os',
  slug: 'axes-and-os',
  contentType: 'experience',
  name: 'Axes & O’s',
  category: 'competition',
  date: '2026-04-23',
  summary: 'Ben had been lobbying for an axe-throwing happy hour for quite some time. His pitch was simple: “Booze and weapons. What could go wrong?” Once we finally coordinated the calendars, the Reckon crew headed to Axes & O’s for an evening of bar food, friendly competition, and learning the difference between a satisfying thunk and an unmistakable miss.',
  place: {
    name: 'Axes & O’s',
    city: 'Sterling',
    state: 'Virginia',
  },
  heroImage: mediaRef('adventures/other-fun/axes-and-os-sterling-va.png'),
  tags: ['axe-throwing', 'team-building', 'competition', 'indoor', 'local-event'],
  storySections: [
    {
      heading: 'Ben’s Idea',
      paragraphs: [
        'Axes & O’s was Ben’s idea. He had been asking us to try it as a happy hour and friendly competition for quite some time. On April 23, 2026, we finally managed to coordinate everyone’s schedules and turn his suggestion into an official Reckon event.',
        'As Ben put it: “Booze and weapons. What could go wrong?”',
        'Axes & O’s occupies an otherwise unremarkable spot in an industrial business park in Sterling, Virginia. There is plenty of parking, but little outside to hint at what is waiting within. The most noticeable feature is the company’s name, AXES & O’s Axe Throwing Company, mounted on the brick facade.',
        'Inside, the crew gathered around the small bar just to the left of the entrance. We ordered drinks, waited for the remaining arrivals, and made sure everyone signed the required waiver before we began.',
      ],
    },
    {
      heading: 'Learning the Throw',
      paragraphs: [
        'Once the entire group was checked in, an instructor led us to the throwing lanes in the back. The lanes near the entrance were used for leagues and competitions. We had two adjacent lanes reserved for our group.',
        'Each lane came with four axes: a larger beginner axe and a competition axe on either side. We started with the beginner axes while the instructor demonstrated the proper two-handed overhead technique.',
        'The mechanics were more deliberate than simply heaving an axe at a wooden board. You raised the axe over your head until the back of its head rested squarely between your shoulder blades. From there, you stepped forward, kept your chest facing the target, and brought your arms through in one smooth motion.',
        'The key was not to grip the handle too tightly. You had to let the axe’s momentum carry it forward and allow the handle to slide naturally from your hands.',
        'It was less about brute force than trusting the movement and finding the right release point.',
      ],
    },
    {
      heading: 'Thunks, Clunks, and Thuds',
      paragraphs: [
        'You did not need to see the target to know whether a throw had worked.',
        'When the blade struck cleanly and stuck in the board, it made a loud, satisfying thunk. When it failed to stick, there was first a clunk against the target, followed by the dull thud of the axe landing on the floor.',
        'For a while, those sounds provided the rhythm of the evening.',
        'Everyone took turns practicing the overhead throw, adjusting their stance, grip, and release. A small change could be the difference between watching the axe tumble away and seeing it remain planted in the board.',
        'Each successful throw brought a little more confidence. Each miss offered another chance to make an adjustment and try again.',
      ],
    },
    {
      heading: 'Clearing the Board',
      paragraphs: [
        'After we had practiced for a while and placed our food order, the instructor introduced a simple team game that worked much like darts.',
        'The goal was to hit each target area three times. The first team to clear every area of the board won.',
        'That became a round-robin tournament, played while we ate mostly standard bar food between turns. The exact teams and final results have faded from memory, but the scoreboard was never the important part. It was a fun, unfamiliar challenge with just enough competition to keep everyone engaged.',
        'For a brief moment after a clean throw, any one of us could feel as though we had figured it out. Then the next axe would clatter to the floor and restore the proper level of humility.',
      ],
    },
    {
      heading: 'Watching the Regulars',
      paragraphs: [
        'When our reserved time ended, we carried the remaining food back to the bar and ordered another drink.',
        'By then, league play had begun in the front lanes. The recreational groups had given way to people who arrived with their own axes and threw with an entirely different level of consistency.',
        'We stayed for a while and watched them repeatedly hit their targets. After spending the evening celebrating every axe that happened to stick, seeing experienced competitors place throw after throw made the distinction between beginner’s luck and genuine skill very clear.',
        'It was a fitting end to the night. We had arrived as complete beginners, learned enough to compete with one another, and then watched people who took the sport seriously show us how it was actually done.',
        'Ben had been right: axe throwing made for a very good Reckon happy hour. It was new, challenging, social, and competitive without requiring anyone to take the result too seriously.',
        'And, against the odds, nothing went wrong.',
      ],
    },
  ],
  featured: true,
};

export const k1Speed = {
  id: 'k1-speed',
  slug: 'k1-speed',
  contentType: 'experience',
  name: 'K1 Speed',
  category: 'competition',
  date: '2024-10-08',
  summary: 'Al’s idea for a Reckon outing took us to K1 Speed for an evening of qualifying laps, minor collisions, and friendly competition. A large turnout meant dividing the crew into two groups before bringing the fastest drivers together for the final. Phillip and Brad set the early pace, but Phillip ultimately earned the top spot on the podium.',
  place: {
    name: 'K1 Speed',
    city: 'Sterling',
    state: 'Virginia',
  },
  heroImage: mediaRef('insta-1.png'),
  tags: ['racing', 'team-building', 'competition', 'indoor', 'local-event'],
  meritBadgeIds: [],
  storySections: [
    {
      heading: 'Al’s Idea',
      paragraphs: [
        'K1 Speed was Al’s idea.',
        'On Tuesday, October 8, 2024, a larger-than-usual Reckon crew gathered for an evening of indoor go-kart racing in Sterling, Virginia.',
        'Like several of our favorite local discoveries, K1 Speed occupies a fairly nondescript office park. There is little outside to convey the noise, speed, and competition waiting beyond the entrance.',
        'After arriving, everyone signed the required waivers and headed into the track area. From there, we could watch other racers making their way around the course while we waited for the rest of our group to arrive.',
        'Watching from the sidelines gave us an early look at the track and gave everyone time to start forming opinions about how fast they might be once it was their turn.',
      ],
    },
    {
      heading: 'Dividing the Field',
      paragraphs: [
        'Once the entire crew had assembled, we learned that our group was too large to race together in a single session. We would need to split into two qualifying groups.',
        'The first group was fitted with helmets and brought into the pit area, where each driver climbed into a kart. Before anyone was released onto the track, K1 gave us a quick safety briefing. The staff explained the basic rules and walked us through the different flags we might see during a race.',
        'Then the first group lined up in the pit and waited to be sent onto the course.',
        'Splitting the field added another dimension to the outing. While one group raced, the other could watch from the track area, follow the developing competition, and study where drivers were gaining or losing time.',
      ],
    },
    {
      heading: 'Qualifying',
      paragraphs: [
        'The opening sessions served as qualifying runs.',
        'The faster drivers from each group would advance into an upper final, while the remaining drivers would be combined in a second race. Instead of two unrelated heats, the format gave everyone a chance to qualify against their initial group and then race people posting similar times.',
        'The qualifying rounds were not entirely clean. There were a few minor crashes as drivers learned the course, tested the limits of the karts, and discovered that a line through a corner could disappear quickly when a coworker was occupying it.',
        'Amid the spins, bumps, and adjustments, two names began to separate from the rest of the field. Phillip and Brad emerged with the fastest qualifying times and established themselves as the drivers to beat in the final.',
      ],
    },
    {
      heading: 'The Final',
      paragraphs: [
        'With qualifying complete, the groups were reshuffled according to their times. The quickest racers came together for the upper final, turning the friendly company outing into a more focused competition.',
        'Phillip carried his qualifying speed into the final and finished the night as the winner.',
        'K1 had a proper podium waiting at the end, giving the finalists a chance to stand in their finishing positions. The full order may eventually be recovered from a photograph, but the result that survived was the important one: Phillip took the top step.',
        'It was a fitting conclusion to Al’s idea. We had arrived at an anonymous Sterling office park, signed our waivers, and divided into two groups simply because there were too many of us to race at once. By the end of the evening, we had qualifying times, finalists, a winner, and another Reckon competition that would be remembered long after the exact standings were forgotten.',
      ],
    },
  ],
  featured: false,
};

export const movieNightAlamo = {
  id: 'movie-night-alamo',
  slug: 'movie-night-alamo',
  contentType: 'experience',
  name: 'Movie Night at Alamo',
  category: 'activity',
  date: '',
  summary: 'A relaxed team movie night close to home.',
  place: { name: 'Alamo Drafthouse' },
  tags: ['movies', 'team-connection', 'indoor', 'local-event'],
  meritBadgeIds: [],
  featured: false,
};

export const experiences = [axesAndOs, k1Speed];
export const experiencesBySlug = experiences.reduce((lookup, experience) => ({ ...lookup, [experience.slug]: experience }), {});

export default experiences;
